import { Image, ImageProps, Link, LinkProps, ListItem, Stack, Text, TextProps, UnorderedList } from "@chakra-ui/react";
import { ContentWidthContainer } from "../components/ContentWidthContainer";
import { PageHeader } from "../components/PageHeader";
import { SubHeaderText } from "../components/StaticPageComponents";
import insuranceImage1 from "../images/insurance-main.jpg";
import insuranceImage2 from "../images/insurance-image2.jpg";
import insuranceImage3 from "../images/insurance-image3.jpg";
import insuranceImage4 from "../images/insurance-image4.jpg";

const INSURANCE_LINK = "https://www.insuremy.co.uk/caravan-insurance?mkcode=breakfreecampaign";
const BLOGS_MAX_WIDTH = "800px";

export const Insurance = () => (
  <>
    <PageHeader maxW={BLOGS_MAX_WIDTH}>Protect Your New Caravan With InsureMy Caravan Insurance</PageHeader>
    <ContentWidthContainer maxW={BLOGS_MAX_WIDTH}>
      <Stack spacing={4}>
        <ImageComp src={insuranceImage1} />
        <SubHeaderText mb={0}>So, you’ve just bought a caravan – congratulations!</SubHeaderText>
        <Text>
          Whether you’ve chosen a touring van for spontaneous weekend getaways or a static caravan for luxurious stays
          in your favourite spot, you’re all set to enjoy the freedom of the open road (or your own peaceful pitch).
          Caravans are towed by a car or other vehicle, and both vehicles and caravans have different insurance needs.
          Now there’s just one more essential thing to tick off your list:{" "}
          <InsureMyLink>
            <Bold>caravan insurance.</Bold>
          </InsureMyLink>{" "}
          The best part? You can save 20% on your insurance policy with Insure My!
        </Text>
        <Text>
          It’s important to understand the difference between caravan insurance and motorhome insurance. Motorhome
          insurance is a legal requirement for vehicles that are driven on the road, while caravan insurance is not a
          legal requirement, but it is highly recommended to protect your investment. You need specific caravan
          insurance because a car insurance policy or standard car insurance typically only covers the towing vehicle,
          not the caravan itself. Being properly insured ensures you are protected against theft, damage, and other
          risks.
        </Text>
        <Text>
          That’s where our trusted partner <InsureMyLink>InsureMy</InsureMyLink> comes in, offering standard policy
          features and making sure you are fully insured. It’s easy to get insurance for your caravan with InsureMy.
        </Text>
        <ImageComp src={insuranceImage2} />
        <SubHeaderText mb={0}>
          Understanding <InsureMyLink>Caravan Insurance</InsureMyLink> Cover
        </SubHeaderText>
        <Text>
          Caravan insurance cover is designed to give caravan owners peace of mind, whether you’re hitting the road with
          a touring caravan or relaxing in your static caravan. This type of insurance policy protects your investment
          from a range of risks, including theft, accidental damage, and unpredictable weather events. With the right
          insurance cover in place, you can enjoy your caravan adventures knowing you’re protected against the
          unexpected.
        </Text>
        <Text>
          When exploring caravan insurance policies, it’s important to look at the level of cover provided. Many
          policies offer “new for old cover,” which means if your caravan is damaged beyond repair or stolen, you could
          receive a brand new replacement of the same make and model, rather than just the current market value.
          Alternatively, some insurance policies cover your caravan for its market value, reflecting what it’s worth at
          the time of a claim.
        </Text>
        <Text>
          Another essential feature to consider is public liability cover, which protects you if someone is injured or
          their property is damaged while in or around your caravan. This is especially important for both touring
          caravans and static caravans, as accidents can happen at any time.
        </Text>
        <Text>
          Comparing caravan insurance policies helps you find the right balance of cover for your needs, whether you
          want comprehensive protection or just the essentials. By understanding what’s included, like new for old,
          market value, and public liability cover. You can make sure your caravan is properly protected, giving you the
          confidence to enjoy every trip, year after year.
        </Text>
        <ImageComp src={insuranceImage3} />
        <SubHeaderText mb={0}>Why Choose InsureMy?</SubHeaderText>
        <Text>InsureMy specialises in protecting static and touring caravans with policies that offer:</Text>
        <UnorderedList>
          <ListItem>Comprehensive coverage for theft, damage, and weather incidents</ListItem>
          <ListItem>Flexible options tailored to how and where you use your caravan</ListItem>
          <ListItem>Peace of mind, whether your caravan is parked up or ready to roll</ListItem>
          <ListItem>
            Optional extras such as breakdown cover, European cover, and enhanced security devices for added protection
          </ListItem>
          <ListItem>Exclusive: 20% off all policies with Breakfree Holidays</ListItem>
        </UnorderedList>
        <Text>
          InsureMy offers discounts for careful owners, club members, and those who use approved security devices or
          secure storage. You can easily compare caravan insurance prices and quotes with InsureMy, helping you find the
          best price and coverage for the value of your caravan. InsureMy covers a wide range of risks and offers
          flexible options to suit your needs, making finding the right policy simple.
        </Text>
        <Text>
          If you’ve purchased your caravan through Breakfree Holidays, <Bold>InsureMy is your next stop</Bold> to ensure
          your new holiday lifestyle is covered from day one.
        </Text>
        <InsureMyLink>
          🎉 Tap here to get a quote and save <Bold>20%, exclusively with Breakfree!</Bold>
        </InsureMyLink>
        <ImageComp src={insuranceImage4} />
      </Stack>
    </ContentWidthContainer>
  </>
);

const Bold: React.FC<TextProps> = (props) => <Text as="span" fontWeight="bold" {...props} />;
const InsureMyLink: React.FC<LinkProps> = (props) => (
  <Link isExternal fontWeight="bold" color="blue.500" href={INSURANCE_LINK} {...props} />
);
const ImageComp: React.FC<ImageProps> = (props) => <Image w="80%" mx="auto" my={4} {...props} />;
